import { db } from '@/firebaseConfig'

function criarErroConflito(message = 'Este horário já foi preenchido. Por favor, escolha outro.') {
  const error = new Error(message)
  error.status = 409
  return error
}

function normalizarDataHora(value) {
  return String(value || '').replace(' ', 'T')
}

function obterIntervalo(start, end) {
  const inicioNormalizado = normalizarDataHora(start)
  const fimNormalizado = normalizarDataHora(end)
  const inicio = new Date(`${inicioNormalizado}-03:00`).getTime()
  const fim = new Date(`${fimNormalizado}-03:00`).getTime()

  if (!Number.isFinite(inicio) || !Number.isFinite(fim) || fim <= inicio) {
    throw new Error('O intervalo informado é inválido.')
  }

  return { inicio, fim }
}

function intervalosConflitam(startA, endA, startB, endB) {
  const intervaloA = obterIntervalo(startA, endA)
  const intervaloB = obterIntervalo(startB, endB)
  return intervaloA.inicio < intervaloB.fim && intervaloA.fim > intervaloB.inicio
}

function criarLockRef(barbeiroId, data) {
  return db.collection('agendaLocks').doc(`${barbeiroId}_${data}`)
}

function criarReserva(agendaId, start, end) {
  return {
    agendaId,
    start: normalizarDataHora(start),
    end: normalizarDataHora(end)
  }
}

function reservasValidas(lockSnapshot) {
  if (!lockSnapshot.exists) return []
  const reservas = lockSnapshot.data().reservas
  return Array.isArray(reservas) ? reservas : []
}

async function checarAgendaExistente(agenda, ignorarId = null) {
  const snapshot = await db.collection('agenda')
    .where('dataQuery', '==', agenda.dataQuery)
    .where('barbeiro.id', '==', agenda.barbeiro.id)
    .get()

  const conflito = snapshot.docs.some(doc => {
    if (doc.id === ignorarId) return false
    const existente = doc.data()
    try {
      return intervalosConflitam(agenda.start, agenda.end, existente.start, existente.end)
    } catch (error) {
      return false
    }
  })

  if (conflito) {
    throw criarErroConflito()
  }
}

export async function criarAgendamentoNoPortal(agenda) {
  if (!agenda || !agenda.barbeiro || !agenda.barbeiro.id || !agenda.start || !agenda.end) {
    throw new Error('Dados insuficientes para realizar o agendamento.')
  }

  obterIntervalo(agenda.start, agenda.end)
  await checarAgendaExistente(agenda)

  const data = normalizarDataHora(agenda.start).substring(0, 10)
  const agendaRef = db.collection('agenda').doc()
  const lockRef = criarLockRef(agenda.barbeiro.id, data)
  const agendaComId = { ...agenda, id: agendaRef.id }

  await db.runTransaction(async transaction => {
    const lockSnapshot = await transaction.get(lockRef)
    const reservas = reservasValidas(lockSnapshot)
    const conflito = reservas.some(reserva =>
      intervalosConflitam(agenda.start, agenda.end, reserva.start, reserva.end)
    )

    if (conflito) {
      throw criarErroConflito()
    }

    transaction.set(agendaRef, agendaComId)
    transaction.set(lockRef, {
      barbeiroId: agenda.barbeiro.id,
      data,
      versao: Number(lockSnapshot.exists ? lockSnapshot.data().versao || 0 : 0) + 1,
      atualizadoEm: Date.now(),
      reservas: [...reservas, criarReserva(agendaRef.id, agenda.start, agenda.end)]
    }, { merge: true })
  })

  return agendaComId
}

export async function ajustarAgendamentoNoPortal(agenda, horarioInicio, horarioFim) {
  const data = normalizarDataHora(agenda.start).substring(0, 10)
  const agendaAtualizada = {
    ...agenda,
    start: `${data}T${horarioInicio}`,
    end: `${data}T${horarioFim}`
  }
  obterIntervalo(agendaAtualizada.start, agendaAtualizada.end)
  await checarAgendaExistente(agendaAtualizada, agenda.id)

  const agendaRef = db.collection('agenda').doc(agenda.id)
  const lockRef = criarLockRef(agenda.barbeiro.id, data)

  await db.runTransaction(async transaction => {
    const agendaSnapshot = await transaction.get(agendaRef)
    const lockSnapshot = await transaction.get(lockRef)
    if (!agendaSnapshot.exists) {
      throw new Error('Agendamento não encontrado.')
    }

    const reservasSemAtual = reservasValidas(lockSnapshot)
      .filter(reserva => reserva.agendaId !== agenda.id)
    const conflito = reservasSemAtual.some(reserva =>
      intervalosConflitam(
        agendaAtualizada.start,
        agendaAtualizada.end,
        reserva.start,
        reserva.end
      )
    )
    if (conflito) {
      throw criarErroConflito('O novo horário conflita com outro agendamento.')
    }

    transaction.update(agendaRef, {
      start: agendaAtualizada.start,
      end: agendaAtualizada.end
    })
    transaction.set(lockRef, {
      barbeiroId: agenda.barbeiro.id,
      data,
      versao: Number(lockSnapshot.exists ? lockSnapshot.data().versao || 0 : 0) + 1,
      atualizadoEm: Date.now(),
      reservas: [
        ...reservasSemAtual,
        criarReserva(agenda.id, agendaAtualizada.start, agendaAtualizada.end)
      ]
    }, { merge: true })
  })
}

export async function excluirAgendamentoNoPortal(agenda) {
  const data = normalizarDataHora(agenda.start).substring(0, 10)
  const agendaRef = db.collection('agenda').doc(agenda.id)
  const lockRef = criarLockRef(agenda.barbeiro.id, data)

  await db.runTransaction(async transaction => {
    const agendaSnapshot = await transaction.get(agendaRef)
    const lockSnapshot = await transaction.get(lockRef)
    if (!agendaSnapshot.exists) return

    transaction.delete(agendaRef)
    if (lockSnapshot.exists) {
      transaction.update(lockRef, {
        versao: Number(lockSnapshot.data().versao || 0) + 1,
        atualizadoEm: Date.now(),
        reservas: reservasValidas(lockSnapshot)
          .filter(reserva => reserva.agendaId !== agenda.id)
      })
    }
  })
}
